import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useGcDirectHistory } from "@/hooks/use-pay-applications";

function formatDate(date: string | null | undefined): string {
  if (!date) return "--";
  return new Date(date).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAction(action: string): string {
  switch (action) {
    case "gc_direct_line_created":
      return "Created";
    case "gc_direct_line_updated":
      return "Updated";
    case "gc_direct_line_deleted":
      return "Deleted";
    default:
      return action;
  }
}

interface Props {
  payAppId: string;
  lineItemId: string;
  lineDescription: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GcDirectDrillDownSheet({
  payAppId,
  lineItemId,
  lineDescription,
  open,
  onOpenChange,
}: Props) {
  const { data: history, isLoading } = useGcDirectHistory(payAppId, lineItemId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader className="mb-2">
          <DialogTitle>GC Direct — Edit History</DialogTitle>
          <p className="text-sm text-muted-foreground">{lineDescription}</p>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : !history || history.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No edits recorded yet.</p>
        ) : (
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>By</TableHead>
                <TableHead>Changed Fields</TableHead>
                <TableHead>When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="font-medium">{formatAction(entry.action)}</TableCell>
                  <TableCell>{entry.actorName ?? "—"}</TableCell>
                  <TableCell>
                    {entry.changedFields && entry.changedFields.length > 0 ? (
                      <div className="space-y-0.5">
                        {entry.changedFields.map((field) => {
                          const prev = entry.previousState?.[field];
                          const next = entry.newState?.[field];
                          return (
                            <div key={field} className="text-[10px]">
                              <span className="font-medium">{field}</span>:{" "}
                              <span className="text-muted-foreground line-through">
                                {prev !== undefined ? String(prev) : "—"}
                              </span>{" "}
                              → <span>{next !== undefined ? String(next) : "—"}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap">
                    {formatDate(entry.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
